/**
 * Chat Chunk Contract
 *
 * Issue #261: AI-Powered Data Chat Enhancement - Phase 9 (Enhanced Command Palette UX)
 *
 * Defines streaming chunk types for AI chat responses:
 * - Text and thinking chunks
 * - Tool invocation and result chunks
 * - Dynamic UI components
 * - Human-in-the-loop approval requests
 * - RAG source citations
 * - Confidence indicators
 * - Follow-up suggestions
 *
 * Compatible with Vercel AI SDK streaming patterns.
 * @see https://sdk.vercel.ai/docs/ai-sdk-core/streaming
 */

import { z } from 'zod';
import { ConfidenceLevel, ToolExecutionStatus } from '../enums/common';
import type { UIComponent } from './dynamic-ui.contract';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Confidence score thresholds for determining confidence level.
 * Used consistently across contracts and UI components.
 */
export const CONFIDENCE_THRESHOLDS = {
  VERY_HIGH: 0.9,
  HIGH: 0.7,
  MEDIUM: 0.5,
} as const;

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Types of chat chunks that can be streamed from the AI
 */
export enum ChatChunkType {
  // Standard response chunks
  TEXT = 'TEXT',
  THINKING = 'THINKING',
  FINAL_ANSWER = 'FINAL_ANSWER',

  // Tool execution chunks
  TOOL_USE = 'TOOL_USE',
  TOOL_RESULT = 'TOOL_RESULT',

  // Enhanced chunks (Phase 9)
  UI_COMPONENT = 'UI_COMPONENT',
  APPROVAL_REQUEST = 'APPROVAL_REQUEST',
  SOURCES = 'SOURCES',
  CONFIDENCE = 'CONFIDENCE',
  SUGGESTIONS = 'SUGGESTIONS',

  // Control chunks
  ERROR = 'ERROR',
  ABORT = 'ABORT',
}

// ============================================================================
// APPROVAL REQUEST TYPES
// ============================================================================

/**
 * Human-in-the-loop approval request details
 */
export interface ApprovalRequestDetails {
  id: string;
  toolId: string;
  toolName: string;
  message: string;
  parameters: Record<string, unknown>;
  operation: 'create' | 'update' | 'delete' | 'execute';
  resourceType: string;
  resourceId?: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  riskFactors: string[];
  expiresAt: string;
}

// ============================================================================
// SOURCE CITATION TYPES
// ============================================================================

/**
 * Source types for RAG citations
 *
 * Issue #356: Added PERSONA_GUIDE for navigation-aware retrieval
 */
export enum SourceType {
  DOCUMENT = 'document',
  RECORD = 'record',
  KNOWLEDGE_BASE = 'knowledge_base',
  EXTERNAL = 'external',
  PERSONA_GUIDE = 'persona_guide',
}

/**
 * RAG source citation
 *
 * Issue #356: Extended with PERSONA_GUIDE source type for navigation-aware retrieval
 */
export interface SourceCitation {
  id: string;
  title?: string;
  content: string;
  score: number;
  sourceType: SourceType;
  url?: string;
  metadata?: {
    entityType?: string;
    entityId?: string;
    documentId?: string;
    pageNumber?: number;
    highlight?: string;
    /** Issue #356: Persona guide specific metadata */
    persona?: string;
    section?: string;
    routeKey?: string;
    navigationUrl?: string;
    [key: string]: unknown;
  };
}

/**
 * Persona guide source citation (Issue #356)
 *
 * Extended source citation with navigation-aware properties.
 */
export interface PersonaGuideSource extends SourceCitation {
  sourceType: SourceType.PERSONA_GUIDE;
  metadata: SourceCitation['metadata'] & {
    persona: string;
    section: string;
    portal: string;
    routeKey?: string;
    navigationUrl?: string;
    keywords?: string[];
  };
}

// ============================================================================
// CONFIDENCE TYPES
// ============================================================================

/**
 * Response confidence assessment
 */
export interface ConfidenceAssessment {
  /** Overall confidence score (0-1) */
  score: number;

  /** Confidence level label */
  level: `${ConfidenceLevel}`;

  /** Factors contributing to confidence */
  factors: ChatConfidenceFactor[];

  /** Optional explanation */
  explanation?: string;
}

/**
 * Individual confidence factor
 */
export interface ChatConfidenceFactor {
  name: string;
  score: number;
  weight: number;
  description?: string;
}

// ============================================================================
// SUGGESTION TYPES
// ============================================================================

/**
 * Types of follow-up suggestions
 *
 * Issue #356: Added GUIDE for persona guide references
 */
export enum SuggestionType {
  QUESTION = 'question',
  ACTION = 'action',
  EXPLORATION = 'exploration',
  GUIDE = 'guide',
}

/**
 * Follow-up suggestion for the user
 *
 * Issue #356: Extended with GUIDE type for persona guide references
 */
export interface FollowUpSuggestion {
  id: string;
  text: string;
  type: SuggestionType;
  priority: number;
  context?: {
    relatedEntityType?: string;
    relatedEntityId?: string;
    /** Issue #356: Guide reference for GUIDE type suggestions */
    guideReference?: {
      guideId: string;
      persona: string;
      section: string;
      routeKey?: string;
      navigationUrl?: string;
    };
    [key: string]: unknown;
  };
}

// ============================================================================
// MAIN CHAT CHUNK INTERFACE
// ============================================================================

/**
 * Base chat chunk with common properties
 */
export interface ChatChunkBase {
  /** Unique chunk ID for deduplication */
  id?: string;

  /** Chunk type discriminator */
  type: ChatChunkType;

  /** Timestamp when chunk was created */
  timestamp?: number;

  /** Trace ID for observability */
  traceId?: string;
}

/**
 * Text content chunk
 */
export interface TextChatChunk extends ChatChunkBase {
  type: ChatChunkType.TEXT;
  content: string;
  /** Whether this is a delta (incremental) or complete text */
  delta?: boolean;
}

/**
 * Thinking/reasoning chunk (for chain-of-thought visibility)
 */
export interface ThinkingChatChunk extends ChatChunkBase {
  type: ChatChunkType.THINKING;
  content: string;
  step?: number;
  totalSteps?: number;
}

/**
 * Final answer chunk
 */
export interface FinalAnswerChatChunk extends ChatChunkBase {
  type: ChatChunkType.FINAL_ANSWER;
  content: string;
}

/**
 * Tool use announcement chunk
 */
export interface ToolUseChatChunk extends ChatChunkBase {
  type: ChatChunkType.TOOL_USE;
  toolId: string;
  toolName: string;
  parameters: Record<string, unknown>;
  status: `${ToolExecutionStatus}`;
}

/**
 * Tool result chunk
 */
export interface ToolResultChatChunk extends ChatChunkBase {
  type: ChatChunkType.TOOL_RESULT;
  toolId: string;
  toolName: string;
  success: boolean;
  result?: unknown;
  error?: string;
  executionTimeMs?: number;
}

/**
 * Dynamic UI component chunk
 */
export interface UIComponentChatChunk extends ChatChunkBase {
  type: ChatChunkType.UI_COMPONENT;
  component: UIComponent;
}

/**
 * Human-in-the-loop approval request chunk
 */
export interface ApprovalRequestChatChunk extends ChatChunkBase {
  type: ChatChunkType.APPROVAL_REQUEST;
  approvalRequest: ApprovalRequestDetails;
}

/**
 * RAG sources chunk
 */
export interface SourcesChatChunk extends ChatChunkBase {
  type: ChatChunkType.SOURCES;
  sources: SourceCitation[];
  totalSources?: number;
}

/**
 * Confidence assessment chunk
 */
export interface ConfidenceChatChunk extends ChatChunkBase {
  type: ChatChunkType.CONFIDENCE;
  confidence: ConfidenceAssessment;
}

/**
 * Follow-up suggestions chunk
 */
export interface SuggestionsChatChunk extends ChatChunkBase {
  type: ChatChunkType.SUGGESTIONS;
  suggestions: FollowUpSuggestion[];
}

/**
 * Error chunk
 */
export interface ErrorChatChunk extends ChatChunkBase {
  type: ChatChunkType.ERROR;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    retryable?: boolean;
  };
}

/**
 * Abort chunk (stream termination)
 */
export interface AbortChatChunk extends ChatChunkBase {
  type: ChatChunkType.ABORT;
  reason: string;
}

/**
 * Union type of all chat chunks
 */
export type ChatChunk =
  | TextChatChunk
  | ThinkingChatChunk
  | FinalAnswerChatChunk
  | ToolUseChatChunk
  | ToolResultChatChunk
  | UIComponentChatChunk
  | ApprovalRequestChatChunk
  | SourcesChatChunk
  | ConfidenceChatChunk
  | SuggestionsChatChunk
  | ErrorChatChunk
  | AbortChatChunk;

// ============================================================================
// ENHANCED CHAT MESSAGE
// ============================================================================

/**
 * Enhanced chat message with all Phase 9 features
 */
export interface EnhancedChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;

  /** Dynamic UI components rendered in the message */
  uiComponents?: UIComponent[];

  /** Pending approval request (if any) */
  approvalRequest?: ApprovalRequestDetails;

  /** RAG sources used for the response */
  sources?: SourceCitation[];

  /** Response confidence assessment */
  confidence?: ConfidenceAssessment;

  /** Follow-up suggestions */
  suggestions?: FollowUpSuggestion[];

  /** Tool invocations in this message */
  toolInvocations?: Array<{
    toolId: string;
    toolName: string;
    parameters: Record<string, unknown>;
    result?: unknown;
    success: boolean;
    executionTimeMs?: number;
  }>;

  /** Metadata for observability */
  metadata?: {
    traceId?: string;
    spanId?: string;
    model?: string;
    tokenUsage?: {
      prompt: number;
      completion: number;
      total: number;
    };
    latencyMs?: number;
    businessId?: string;
    agentId?: string;
    [key: string]: unknown;
  };
}

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const chatChunkTypeSchema = z.nativeEnum(ChatChunkType);

export const approvalRequestDetailsSchema = z.object({
  id: z.string(),
  toolId: z.string(),
  toolName: z.string(),
  message: z.string(),
  parameters: z.record(z.string(), z.unknown()),
  operation: z.enum(['create', 'update', 'delete', 'execute']),
  resourceType: z.string(),
  resourceId: z.string().optional(),
  riskLevel: z.enum(['low', 'medium', 'high', 'critical']),
  riskFactors: z.array(z.string()),
  expiresAt: z.string(),
});

export const sourceTypeSchema = z.nativeEnum(SourceType);

export const sourceCitationSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  content: z.string(),
  score: z.number().min(0).max(1),
  sourceType: sourceTypeSchema,
  url: z.string().url().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const confidenceFactorSchema = z.object({
  name: z.string(),
  score: z.number().min(0).max(1),
  weight: z.number().min(0).max(1),
  description: z.string().optional(),
});

export const confidenceAssessmentSchema = z.object({
  score: z.number().min(0).max(1),
  level: z.enum(['low', 'medium', 'high', 'very_high']),
  factors: z.array(confidenceFactorSchema),
  explanation: z.string().optional(),
});

export const suggestionTypeSchema = z.nativeEnum(SuggestionType);

export const followUpSuggestionSchema = z.object({
  id: z.string(),
  text: z.string(),
  type: suggestionTypeSchema,
  priority: z.number(),
  context: z.record(z.string(), z.unknown()).optional(),
});

export const chatChunkBaseSchema = z.object({
  id: z.string().optional(),
  type: chatChunkTypeSchema,
  timestamp: z.number().optional(),
  traceId: z.string().optional(),
});

export const enhancedChatMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string(),
  timestamp: z.number(),
  sources: z.array(sourceCitationSchema).optional(),
  confidence: confidenceAssessmentSchema.optional(),
  suggestions: z.array(followUpSuggestionSchema).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Type guard for text chunks
 */
export function isTextChunk(chunk: ChatChunk): chunk is TextChatChunk {
  return chunk.type === ChatChunkType.TEXT;
}

/**
 * Type guard for UI component chunks
 */
export function isUIComponentChunk(chunk: ChatChunk): chunk is UIComponentChatChunk {
  return chunk.type === ChatChunkType.UI_COMPONENT;
}

/**
 * Type guard for approval request chunks
 */
export function isApprovalRequestChunk(chunk: ChatChunk): chunk is ApprovalRequestChatChunk {
  return chunk.type === ChatChunkType.APPROVAL_REQUEST;
}

/**
 * Type guard for sources chunks
 */
export function isSourcesChunk(chunk: ChatChunk): chunk is SourcesChatChunk {
  return chunk.type === ChatChunkType.SOURCES;
}

/**
 * Type guard for confidence chunks
 */
export function isConfidenceChunk(chunk: ChatChunk): chunk is ConfidenceChatChunk {
  return chunk.type === ChatChunkType.CONFIDENCE;
}

/**
 * Type guard for suggestions chunks
 */
export function isSuggestionsChunk(chunk: ChatChunk): chunk is SuggestionsChatChunk {
  return chunk.type === ChatChunkType.SUGGESTIONS;
}

/**
 * Type guard for error chunks
 */
export function isErrorChunk(chunk: ChatChunk): chunk is ErrorChatChunk {
  return chunk.type === ChatChunkType.ERROR;
}

/**
 * Calculate overall confidence level from score.
 * Uses CONFIDENCE_THRESHOLDS constants for consistent scoring.
 */
export function getConfidenceLevel(score: number): ConfidenceAssessment['level'] {
  if (score >= CONFIDENCE_THRESHOLDS.VERY_HIGH) return ConfidenceLevel.VERY_HIGH;
  if (score >= CONFIDENCE_THRESHOLDS.HIGH) return ConfidenceLevel.HIGH;
  if (score >= CONFIDENCE_THRESHOLDS.MEDIUM) return ConfidenceLevel.MEDIUM;
  return ConfidenceLevel.LOW;
}

// ============================================================================
// PERSONA GUIDE HELPERS (Issue #356)
// ============================================================================

/**
 * Type guard for persona guide sources
 */
export function isPersonaGuideSource(source: SourceCitation): source is PersonaGuideSource {
  return source.sourceType === SourceType.PERSONA_GUIDE;
}

/**
 * Type guard for guide suggestions
 */
export function isGuideSuggestion(
  suggestion: FollowUpSuggestion,
): suggestion is FollowUpSuggestion & { type: SuggestionType.GUIDE } {
  return suggestion.type === SuggestionType.GUIDE;
}

/**
 * Create a persona guide source citation
 *
 * Issue #356: Helper to create properly typed persona guide sources
 */
export function createPersonaGuideSource(params: {
  id: string;
  title: string;
  content: string;
  score: number;
  persona: string;
  section: string;
  portal: string;
  routeKey?: string;
  navigationUrl?: string;
  keywords?: string[];
}): PersonaGuideSource {
  return {
    id: params.id,
    title: params.title,
    content: params.content,
    score: params.score,
    sourceType: SourceType.PERSONA_GUIDE,
    url: params.navigationUrl,
    metadata: {
      persona: params.persona,
      section: params.section,
      portal: params.portal,
      routeKey: params.routeKey,
      navigationUrl: params.navigationUrl,
      keywords: params.keywords,
    },
  };
}

/**
 * Create a guide follow-up suggestion
 *
 * Issue #356: Helper to create guide-type suggestions with navigation context
 */
export function createGuideSuggestion(params: {
  id: string;
  text: string;
  priority: number;
  guideId: string;
  persona: string;
  section: string;
  routeKey?: string;
  navigationUrl?: string;
}): FollowUpSuggestion {
  return {
    id: params.id,
    text: params.text,
    type: SuggestionType.GUIDE,
    priority: params.priority,
    context: {
      guideReference: {
        guideId: params.guideId,
        persona: params.persona,
        section: params.section,
        routeKey: params.routeKey,
        navigationUrl: params.navigationUrl,
      },
    },
  };
}
